/**
 * @file AdminLayout 后台壳层
 * @description Sidebar + Topbar + 主内容区；支持侧栏折叠。
 *
 * @whenToUse 管理后台页面根布局（配合 react-router Outlet）。
 * @whenNotToUse 落地营销页、Showcase 单页文档。
 */

import { useState, type ReactNode } from 'react'
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { cn } from '@/lib/cn.ts'
import { IconButton } from '@/components/ui/icon-button.tsx'
import { ScrollArea } from '@/components/ui/scroll-area.tsx'
import { Sidebar, type SidebarItem } from '@/components/ui/sidebar.tsx'
import { Topbar } from '@/components/ui/topbar.tsx'

export type AdminLayoutProps = {
  /** 侧栏菜单 */
  items: SidebarItem[]
  /**
   * 侧栏品牌；可按折叠态渲染（收起时隐藏文字、只留 logo）。
   */
  brand?: ReactNode | ((collapsed: boolean) => ReactNode)
  /** 顶栏左侧（折叠按钮右侧，如面包屑） */
  topbarLeft?: ReactNode
  /** 顶栏右侧 */
  topbarRight?: ReactNode
  /** 主内容 */
  children: ReactNode
  /** 默认是否折叠 */
  defaultCollapsed?: boolean
  className?: string
}

export function AdminLayout({
  items,
  brand,
  topbarLeft,
  topbarRight,
  children,
  defaultCollapsed = false,
  className,
}: AdminLayoutProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed)

  return (
    <div className={cn('flex h-full min-h-0 bg-canvas text-foreground', className)}>
      <Sidebar items={items} brand={brand} collapsed={collapsed} />
      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar
          left={
            <>
              <IconButton
                variant="ghost"
                aria-label={collapsed ? '展开侧栏' : '折叠侧栏'}
                icon={
                  collapsed ? (
                    <PanelLeftOpen className="size-4" aria-hidden />
                  ) : (
                    <PanelLeftClose className="size-4" aria-hidden />
                  )
                }
                onClick={() => setCollapsed((v) => !v)}
              />
              {topbarLeft}
            </>
          }
          right={topbarRight}
        />
        <ScrollArea className="min-h-0 flex-1">
          <main className="p-4">{children}</main>
        </ScrollArea>
      </div>
    </div>
  )
}
